// Which squares can hand material to which.
//
// Material used to move on the ranks and files only, because that is how a
// belt runs. But three of the pieces that handle it are diagonal pieces: a
// bishop cooks, a king cooks, and the queen sweeps and unloads. Making them
// take delivery only from the four cardinal squares meant a lane that ran past
// a bishop's diagonal was useless to it, and a queen ending her sweep on the
// diagonal of a foundry held onto everything she had just collected.
//
// So the rule is: a diagonal link exists when *either end* of it is a bishop,
// queen or king. Either end rather than the sender's end alone, because
// otherwise a pawn diagonally touching a bishop is a dead connection that
// looks exactly like a live one — and "the two things are touching" is how
// players read this, not "which of them is allowed to reach".

import {
  BISHOP,
  BISHOP_DIRS,
  KING,
  type PieceType,
  pieceType,
  QUEEN,
  ROOK_DIRS
} from "../board";
import { hasCell, pieceAt, type Position } from "../position";
import { hasStructure, type IndustryState } from "./state";

/** Pieces that move material on the diagonals as well as the ranks and files. */
export function carriesDiagonally(type: PieceType): boolean {
  return type === BISHOP || type === QUEEN || type === KING;
}

/**
 * Squares `sq` can pass material to or take it from, **cardinals first**.
 *
 * The order is load-bearing: several callers take the first match, and putting
 * the four cardinals ahead of the diagonals means every hand-off that worked
 * before diagonals existed still resolves to the same square. Two clients
 * derive this independently from one log (PLAN §5.5), so it cannot be an
 * unordered set.
 */
export function materialLinks(
  position: Position,
  industry: IndustryState,
  sq: number
): number[] {
  const links: number[] = [];

  for (const offset of ROOK_DIRS) {
    const adj = sq + offset;
    // A building's square is deliberately not a cell — that is the whole of
    // how it blocks movement — so `hasCell` alone would leave every structure
    // on the board unreachable by material, which is most of what one is for.
    if (hasCell(position, adj) || hasStructure(industry, adj)) links.push(adj);
  }

  // **A structure links cardinally and only cardinally.** The diagonal rule
  // exists because a bishop, a queen and a king *reach* diagonally; a building
  // has no such reach, and it is worth being a rule now rather than something
  // decided by whichever caller was written first.
  if (hasStructure(industry, sq)) return links;

  const here = pieceAt(position, sq);
  const reaches = here !== 0 && carriesDiagonally(pieceType(here));

  for (const offset of BISHOP_DIRS) {
    const adj = sq + offset;
    if (!hasCell(position, adj)) continue;
    if (reaches) {
      links.push(adj);
      continue;
    }
    // And not into a structure either: the reach belongs to the piece, and
    // there is no piece on a building's square to have it.
    if (hasStructure(industry, adj)) continue;
    const there = pieceAt(position, adj);
    if (there !== 0 && carriesDiagonally(pieceType(there))) links.push(adj);
  }

  return links;
}
